import {
  LayoutDashboard,
  Building2,
  Users,
  Network,
  SearchCheck,
  FileText,
  ClipboardList,
  ShieldCheck,
  Settings,
  Package,
  Eye,
  Home,
  AlertTriangle,
  ArrowLeftRight,
  BookOpen,
  BarChart3,
  Handshake,
  ShieldPlus,
  Mail,
  Scale,
  TrendingUp,
  Cookie,
  Server,
  Inbox,
  Database,
  Sparkles,
} from "lucide-react";

export const HELP_SECTIONS = [
  { id: "overview", label: "Overview" },
  { id: "getting-started", label: "Getting Started" },
  { id: "roles", label: "Who Does What" },
  { id: "journey", label: "Your Journey" },
  { id: "modules", label: "Features Guide" },
  { id: "use-cases", label: "Real-Life Examples" },
  { id: "best-practices", label: "Tips & Best Practices" },
  { id: "glossary", label: "Simple Glossary" },
  { id: "faq", label: "Common Questions" },
  { id: "troubleshooting", label: "Fix Common Problems" },
];

export const SYSTEM_OVERVIEW = {
  title: "OneDPDP",
  tagline:
    "Your friendly workspace to prepare for India's Digital Personal Data Protection (DPDP) Act",
  description:
    "OneDPDP helps your organisation understand where you stand on privacy, document how personal data is used, manage vendors and breaches, keep policies up to date, and show progress to leadership — all in one place. You do not need to be a lawyer or IT expert to use it. Follow the guided steps, answer plain-language questions, and let the platform track your progress toward the DPDP compliance deadline.",
  deadline: "13 May 2027",
  quickFacts: [
    {
      title: "Who is it for?",
      text: "Compliance teams, privacy officers, department heads, and anyone responsible for data protection in your organisation.",
    },
    {
      title: "What do you do here?",
      text: "Complete assessments, upload policies, report breaches, review consent, and download reports — step by step.",
    },
    {
      title: "How does it help?",
      text: "Everything stays in one secure workspace so audits, board reviews, and day-to-day work are easier to manage.",
    },
  ],
  pillars: [
    {
      title: "Understand & Assess",
      text: "Start with a Gap Assessment to see how ready you are. Then document processing activities (ROPA), high-risk projects (PIA), and vendors (TPRM).",
    },
    {
      title: "Run Day-to-Day Privacy Work",
      text: "Track assets, handle data-subject requests (DSAR), compare consent with your data inventory, manage policies, and respond to breaches on time.",
    },
    {
      title: "See Progress & Share Results",
      text: "Your Home page and Dashboard show what's done and what's next. Reports turn your work into PDFs for management and auditors.",
    },
  ],
};

export const GETTING_STARTED = {
  intro:
    "New here? Follow these simple steps based on your role. Each step links to the right area of the app. You can also use the Interactive Tour tab at the top of this Help page for a click-by-click walkthrough.",
  checklists: [
    {
      role: "admin",
      label: "Super Admin — first-time setup",
      color: "#6366f1",
      steps: [
        "Sign in with your platform email and password (no organisation link needed).",
        "Open Settings and set up email (SMTP) so the platform can send notifications.",
        "Create your first customer organisation under Tenant Admins — choose a simple organisation name they will remember.",
        "Set how many users they can add and turn on the modules they purchased (Consent, DSAR, DSPM, DPIA, etc.).",
        "Share the organisation login link and admin credentials with the customer.",
        "Check Licenses regularly to see if any organisation is running out of user slots.",
      ],
    },
    {
      role: "tenant_admin",
      label: "Organisation Admin — get your team ready",
      color: "#0ea5e9",
      steps: [
        "Sign in using the login link your Super Admin gave you.",
        "Create Departments that match how your company is organised (e.g. HR, Marketing, IT).",
        "Add Users who will complete compliance work — each person gets their own login.",
        "Review Form Questions for Gap Assessment, ROPA, PIA, and TPRM — adjust wording if needed.",
        "Configure Settings: your organisation email, optional AI for policy drafts, and breach reminders.",
        "Open Gap Assessment Admin and start a new assessment cycle with start and end dates.",
        "Tell your team to log in and begin from their Home page.",
      ],
    },
    {
      role: "user",
      label: "Team Member — start your compliance work",
      color: "#059669",
      steps: [
        "Sign in with the login link and password from your Organisation Admin.",
        "Open Home to see your personal roadmap and what to do next.",
        "Complete Gap Assessment first — pick your department and answer section by section.",
        "Fill in ROPA (what data you process), PIA (high-risk projects), and TPRM (vendors) when asked.",
        "Use Breach Management if a data incident happens — the app tracks the 72-hour notification clock.",
        "Keep policies updated in Policy Store and download Reports when leadership needs an update.",
      ],
    },
  ],
};

export const ROLES = [
  {
    id: "admin",
    label: "Super Admin",
    badge: "Platform owner",
    color: "#6366f1",
    home: "/admin",
    description:
      "You run the whole OneDPDP platform. You create customer organisations, set their plans, turn features on or off, and make sure email works for everyone.",
    plainSummary:
      "Think of this as the control room for the entire product — one level above any single company.",
    responsibilities: [
      "Create and manage customer organisations (tenants)",
      "Set user limits and enable modules (Consent, DSAR, DSPM, DPIA, etc.)",
      "Configure platform-wide email and branding",
      "Monitor license usage across all organisations",
    ],
    navItems: ["Dashboard", "Tenant Admins", "Licenses", "Settings", "Help"],
  },
  {
    id: "tenant_admin",
    label: "Organisation Admin",
    badge: "Your company",
    color: "#0ea5e9",
    home: "/tenant",
    description:
      "You manage privacy compliance for one organisation. You set up departments and users, customise forms, run assessment cycles, and review what your team submits.",
    plainSummary:
      "You are the go-to person inside your company for setting up OneDPDP and keeping the programme on track.",
    responsibilities: [
      "Add and manage team members (users)",
      "Create departments and assign ownership",
      "Customise assessment questions and open assessment cycles",
      "Review submissions, gaps, and evidence",
      "Configure organisation email, AI, policy prompt, and notifications",
    ],
    navItems: [
      "Dashboard",
      "Users & Departments",
      "Gap / PIA / TPRM Tracking",
      "User Responses & Form Questions",
      "License",
      "Settings",
      "Help",
    ],
  },
  {
    id: "user",
    label: "Team Member",
    badge: "Daily user",
    color: "#059669",
    home: "/user",
    description:
      "You do the hands-on compliance work — answering assessments, documenting processing, reporting breaches, updating policies, and generating reports for your department.",
    plainSummary:
      "You use the tools your Organisation Admin set up. Your Home page tells you what to work on next.",
    responsibilities: [
      "Complete Gap Assessment, ROPA, PIA, and TPRM forms",
      "Track IT assets and data locations (Asset Tracker)",
      "Handle data-subject requests (DSAR) and consent checks",
      "Report and follow up on data breaches",
      "Maintain policies and export compliance reports",
    ],
    navItems: [
      "Home",
      "Dashboard",
      "Assessment (Gap / ROPA / PIA / TPRM)",
      "Asset Tracker",
      "Breach Management",
      "Consent Variance",
      "DSAR",
      "Policy Store",
      "Reports",
      "Help",
    ],
  },
];

export const WORKFLOW_PHASES = [
  {
    step: 1,
    title: "Set up the platform",
    role: "Super Admin",
    summary: "Create the organisation and turn on the right features",
    details:
      "The Super Admin creates your organisation, sets how many people can use it, and enables modules like Consent, DSAR, or DSPM. You receive a login link and admin account to get started.",
    path: "/admin/tenants",
  },
  {
    step: 2,
    title: "Prepare your organisation",
    role: "Organisation Admin",
    summary: "Departments, users, forms, and email",
    details:
      "Your Organisation Admin adds departments, creates user accounts, reviews default questions, sets up email notifications, and opens an assessment cycle so your team knows when to submit work.",
    path: "/tenant/departments",
  },
  {
    step: 3,
    title: "Find your starting point",
    role: "Team Member",
    summary: "Home page shows your roadmap",
    details:
      "When you log in, Home shows how far along you are and recommends the next task. Most organisations start with Gap Assessment to understand their privacy maturity.",
    path: "/user",
  },
  {
    step: 4,
    title: "Complete assessments",
    role: "Team Member",
    summary: "Gap Assessment, ROPA, PIA, and TPRM",
    details:
      "Answer guided questions about your department's data practices. Save as you go. Submit when complete. Your Organisation Admin can review everything under User Responses.",
    path: "/user/gap-assessment",
  },
  {
    step: 5,
    title: "Handle ongoing privacy tasks",
    role: "Team Member",
    summary: "Assets, consent, DSAR, breaches, policies",
    details:
      "Use Asset Tracker for systems that hold personal data, Consent Variance to spot mismatches, DSAR for citizen requests, Breach Management for incidents, and Policy Store for documents.",
    path: "/user/breach-management",
  },
  {
    step: 6,
    title: "Review and report",
    role: "Everyone",
    summary: "Dashboard metrics and PDF reports",
    details:
      "Dashboard shows live numbers. Reports creates Executive, Summary, or Detailed PDFs for leadership, auditors, or the board. Track progress toward 13 May 2027 on Home.",
    path: "/user/reports",
  },
];

export const MODULES = [
  {
    id: "admin-dashboard",
    roles: ["admin"],
    icon: LayoutDashboard,
    color: "#6366f1",
    title: "Admin Dashboard",
    location: "Top menu → Dashboard",
    path: "/admin",
    purpose:
      "A quick snapshot of how many organisations use the platform and how licenses are being used.",
    features: [
      "Total and active organisations",
      "User counts across the platform",
      "License usage summary",
      "Shortcuts to manage tenants",
    ],
    steps: [
      "Sign in as Super Admin.",
      "Review the summary cards on the dashboard.",
      "Use quick links to open Tenants or Licenses when something needs attention.",
    ],
    inputs: ["Platform login"],
    outputs: ["Platform-wide statistics"],
    connections: ["Tenant Admins", "Licenses", "Settings"],
  },
  {
    id: "tenants",
    roles: ["admin"],
    icon: Building2,
    color: "#f97316",
    title: "Tenant Management",
    location: "Top menu → Tenant Admins",
    path: "/admin/tenants",
    purpose:
      "Create and manage customer organisations — each gets its own secure workspace.",
    features: [
      "Add organisation with admin login details",
      "Set user limits and plan expiry",
      "Enable modules: Consent, DSAR, DSPM, DPIA, Gap, ROPA, etc.",
      "Edit, suspend, or deactivate organisations",
    ],
    steps: [
      "Click Add Tenant and enter company name and admin email/password.",
      "Set license limits and expiry date.",
      "Turn on the modules the customer purchased.",
      "Save — the system creates their workspace automatically.",
      "Send the customer their login link and admin credentials.",
    ],
    inputs: ["Company details", "Admin credentials", "License limits", "Module toggles"],
    outputs: ["New organisation", "Organisation admin account"],
    connections: ["Licenses", "Organisation setup", "Platform email"],
  },
  {
    id: "admin-users",
    roles: ["admin"],
    icon: Users,
    color: "#8b5cf6",
    title: "Users Overview",
    location: "Admin area → Users (via tenant detail)",
    path: "/admin/users",
    purpose:
      "See users across organisations in read-only mode — helpful for support and license checks.",
    features: [
      "Browse users by organisation",
      "View account status",
      "Support troubleshooting without accessing private form data",
    ],
    steps: [
      "Open a tenant from Tenant Admins.",
      "Review user list and status.",
      "Use this for support — not for day-to-day user management (tenants manage their own users).",
    ],
    inputs: ["Tenant selection"],
    outputs: ["Cross-tenant user visibility"],
    connections: ["Tenants", "Licenses"],
  },
  {
    id: "licenses-admin",
    roles: ["admin"],
    icon: ShieldCheck,
    color: "#8b5cf6",
    title: "License Management",
    location: "Top menu → Licenses",
    path: "/admin/licenses",
    purpose: "See how many user slots each organisation has used.",
    features: [
      "Per-organisation user limits",
      "Users created vs. limit",
      "Usage percentage at a glance",
    ],
    steps: [
      "Open Licenses from the top menu.",
      "Check each organisation's slot usage.",
      "Increase limits by editing the tenant if a customer needs more users.",
    ],
    inputs: ["Tenant license settings"],
    outputs: ["Usage metrics"],
    connections: ["Tenants", "Organisation Users"],
  },
  {
    id: "admin-settings",
    roles: ["admin"],
    icon: Settings,
    color: "#64748b",
    title: "Platform Settings",
    location: "Top menu → Settings",
    path: "/admin/settings",
    purpose:
      "Set up platform email, branding, and view delivery logs — the fallback for all organisations.",
    features: [
      "Your admin profile",
      "Platform SMTP (outgoing email server)",
      "Default email header and footer",
      "Email activity logs",
    ],
    steps: [
      "Open Settings → enter SMTP details (host, port, username, password).",
      "Enable TLS for secure sending.",
      "Set default email branding under Email Templates.",
      "Send a test email and check Email Logs to confirm delivery.",
    ],
    inputs: ["SMTP credentials", "Email branding"],
    outputs: ["Platform email configuration", "Delivery logs"],
    connections: ["All system emails", "Organisation fallback SMTP"],
  },
  {
    id: "admin-email-templates",
    roles: ["admin"],
    icon: Mail,
    color: "#0284c7",
    title: "Platform Email Templates",
    location: "Settings → Email Templates",
    path: "/admin/email-templates",
    purpose: "Set the default look of system emails sent from the platform.",
    features: ["Header and footer branding", "Colour presets", "Live preview", "Test send"],
    steps: [
      "Open Email Templates from Settings.",
      "Choose colours and add your platform logo text.",
      "Preview how emails will look.",
      "Save and send a test email to yourself.",
    ],
    inputs: ["Branding choices", "SMTP configured"],
    outputs: ["Default email appearance"],
    connections: ["Settings", "Email Logs"],
  },
  {
    id: "tenant-dashboard",
    roles: ["tenant_admin"],
    icon: LayoutDashboard,
    color: "#6366f1",
    title: "Organisation Dashboard",
    location: "Top menu → Dashboard",
    path: "/tenant",
    purpose: "See how many users you have, how much of your license is used, and jump to common tasks.",
    features: [
      "Active user count",
      "License usage bar",
      "Recent users",
      "Quick actions (add user, settings, etc.)",
    ],
    steps: [
      "Sign in with your organisation login link.",
      "Review user count and license usage.",
      "Use Quick Actions for everyday admin tasks.",
    ],
    inputs: ["Organisation admin login"],
    outputs: ["Organisation summary"],
    connections: ["Users", "License", "Departments"],
  },
  {
    id: "tenant-users",
    roles: ["tenant_admin"],
    icon: Users,
    color: "#f43f5e",
    title: "User Management",
    location: "Tenant Admins → Users",
    path: "/tenant/users",
    purpose: "Create and manage the people in your organisation who will use OneDPDP.",
    features: [
      "Add users with name, email, and password",
      "Edit details or reset passwords",
      "Activate or deactivate accounts",
      "Search and paginate the user list",
    ],
    steps: [
      "Click Add User — enter name, email, and password (at least 8 characters).",
      "Share the organisation login link and their credentials.",
      "Use Edit to update details; use Deactivate to block login without deleting history.",
    ],
    inputs: ["Name", "Email", "Password", "Phone (optional)"],
    outputs: ["User accounts", "Welcome emails (if SMTP is set up)"],
    connections: ["All user features", "License limits", "Departments"],
  },
  {
    id: "departments",
    roles: ["tenant_admin"],
    icon: Network,
    color: "#14b8a6",
    title: "Departments",
    location: "Tenant Admins → Departments",
    path: "/tenant/departments",
    purpose:
      "Group your organisation into teams (e.g. HR, Sales) so assessments and reports can be scoped by department.",
    features: [
      "Add departments with name, code, and owner",
      "Bulk import from CSV",
      "Activate or deactivate departments",
    ],
    steps: [
      "Click Add Department — enter name, code, and owner.",
      "Or use Bulk Import for many departments at once.",
      "Assign users to departments when creating or editing their accounts.",
    ],
    inputs: ["Department name", "Code", "Owner name"],
    outputs: ["Department list used in assessments and reports"],
    connections: ["Gap Assessment", "User Responses", "Consent Variance", "Reports"],
  },
  {
    id: "gap-admin",
    roles: ["tenant_admin"],
    icon: SearchCheck,
    color: "#06b6d4",
    title: "Gap Assessment Admin",
    location: "Tenant Admins → Gap Assessment",
    path: "/tenant/gap-assessment",
    purpose:
      "Run privacy maturity assessments — open cycles, watch department progress, review gaps and evidence.",
    features: [
      "Start and close assessment cycles with dates",
      "Department scores and charts",
      "Gap tracking and recommendations",
      "Evidence review and submission history",
    ],
    steps: [
      "Click New Cycle and set start and end dates.",
      "Tell your team to complete Gap Assessment before the deadline.",
      "Monitor progress on Overview, Departments, and Users tabs.",
      "Review gaps and evidence as answers come in.",
      "Close the cycle when everyone has submitted.",
    ],
    inputs: ["Cycle dates", "User submissions", "Evidence files"],
    outputs: ["Department scores", "Gap reports", "Trend charts"],
    connections: ["User Gap Assessment", "Policy Store", "Reports"],
  },
  {
    id: "pia-tracking",
    roles: ["tenant_admin"],
    icon: ShieldPlus,
    color: "#818cf8",
    title: "PIA Tracking",
    location: "Tenant Admins → PIA Tracking",
    path: "/tenant/pia-tracking",
    purpose:
      "Manage Privacy Impact Assessment cycles — same idea as Gap Admin, but for PIA submissions per department.",
    features: [
      "Open/close PIA assessment cycles",
      "Lock or reopen submissions",
      "Department progress and risk scores",
      "Charts for completion status",
    ],
    steps: [
      "Open PIA Tracking from Tenant Admins.",
      "Start a new cycle when teams need to submit PIAs.",
      "Monitor which departments have submitted or are still in progress.",
      "Lock submissions when review is complete.",
    ],
    inputs: ["Cycle dates", "PIA submissions"],
    outputs: ["PIA completion tracking", "Risk overview"],
    connections: ["User PIA", "User Responses", "Reports"],
  },
  {
    id: "tprm-tracking",
    roles: ["tenant_admin"],
    icon: Handshake,
    color: "#0ea5e9",
    title: "TPRM Tracking",
    location: "Tenant Admins → TPRM Tracking",
    path: "/tenant/tprm-tracking",
    purpose:
      "Manage third-party risk assessment cycles — track vendor questionnaires by department.",
    features: [
      "Open/close TPRM cycles",
      "Lock or reopen vendor assessments",
      "Department progress and risk scores",
      "Completion charts",
    ],
    steps: [
      "Open TPRM Tracking from Tenant Admins.",
      "Start a cycle when vendors need to be assessed.",
      "Review department progress and overall risk scores.",
      "Lock submissions after review.",
    ],
    inputs: ["Cycle dates", "TPRM submissions"],
    outputs: ["Vendor risk tracking", "Completion metrics"],
    connections: ["User TPRM", "User Responses", "Reports"],
  },
  {
    id: "form-questions",
    roles: ["tenant_admin"],
    icon: FileText,
    color: "#6366f1",
    title: "Form Questions",
    location: "Tenant Admins → Form Questions",
    path: "/tenant/form-questions/gap_assessment",
    purpose:
      "Customise the questions your team sees in Gap Assessment, ROPA, PIA, and TPRM forms.",
    features: [
      "Add, edit, or deactivate questions",
      "Group questions by section",
      "Different field types (text, dropdown, yes/no, etc.)",
      "Import defaults or bulk upload from CSV",
    ],
    steps: [
      "Pick a form tab: Gap Assessment, ROPA, PIA, or TPRM.",
      "Use Import Defaults to start from built-in DPDP questions, then edit as needed.",
      "Add questions with section name, question text, and required flag.",
      "Deactivate old questions instead of deleting — this keeps past submissions valid.",
    ],
    inputs: ["Question text", "Section", "Field type", "Options"],
    outputs: ["Customised forms for your team"],
    connections: ["All assessment forms", "User Responses"],
  },
  {
    id: "form-responses",
    roles: ["tenant_admin"],
    icon: Eye,
    color: "#6366f1",
    title: "User Responses",
    location: "Tenant Admins → User Responses",
    path: "/tenant/form-responses",
    purpose: "See everything your team has submitted — Gap, ROPA, PIA, and TPRM — in one place.",
    features: [
      "Filter by form type",
      "View full submission detail",
      "Export PDF for audit records",
      "See scores, progress, and status",
    ],
    steps: [
      "Choose a form type tab.",
      "Browse the list of submissions.",
      "Click View on any row to read all answers.",
      "Export PDF if you need a file for auditors.",
    ],
    inputs: ["Team submissions"],
    outputs: ["Audit trail", "PDF exports"],
    connections: ["All assessment modules", "Gap / PIA / TPRM Tracking"],
  },
  {
    id: "tenant-license",
    roles: ["tenant_admin"],
    icon: Package,
    color: "#a855f7",
    title: "License & Usage",
    location: "Top menu → License",
    path: "/tenant/license",
    purpose: "See your plan, expiry date, and how many user slots you have left.",
    features: ["Plan type", "Expiry date", "Users created vs. limit", "Visual usage bar"],
    steps: [
      "Open License from the top menu.",
      "Check Slots Left before adding many new users.",
      "Contact your Super Admin or account manager if you need more slots.",
    ],
    inputs: ["License assigned by Super Admin"],
    outputs: ["Usage visibility"],
    connections: ["User Management", "Admin Licenses"],
  },
  {
    id: "tenant-settings",
    roles: ["tenant_admin"],
    icon: Settings,
    color: "#64748b",
    title: "Organisation Settings",
    location: "Top menu → Settings",
    path: "/tenant/settings",
    purpose:
      "Set up your organisation profile, email, AI for policy drafts, the policy prompt, and breach reminder timing.",
    features: [
      "Profile and organisation name",
      "Organisation SMTP (your own email server)",
      "Policy generation prompt for this organisation",
      "AI provider for Policy Store drafts",
      "Breach notification reminder interval",
      "Email activity logs",
    ],
    steps: [
      "Configure Organisation SMTP if you want emails from your domain.",
      "Send a test email and check Email Activities Logs.",
      "Add an AI provider (OpenAI, Azure, etc.) to enable AI Generate in Policy Store.",
      "Optionally customise the Policy Generation Prompt used for AI policies.",
      "Set how often breach reminders are sent for open incidents.",
    ],
    inputs: ["SMTP settings", "AI API key", "Policy generation prompt", "Reminder interval"],
    outputs: ["Organisation email and AI configuration"],
    connections: ["Policy Store", "User emails", "Breach Management"],
  },
  {
    id: "tenant-policy-prompt",
    roles: ["tenant_admin"],
    icon: Sparkles,
    color: "#7c3aed",
    title: "Policy Generation Prompt",
    location: "Settings → Policy Generation Prompt",
    path: "/tenant/policy-prompt",
    purpose:
      "Customise the AI prompt used to generate DPDP policy documents for this organisation. If you do not save a custom prompt, the built-in default is used.",
    features: [
      "Edit the system prompt used for this organisation's AI policies",
      "Choose how many sentences each generated section should have",
      "Monitor tokens used, tokens left, and what each policy costs",
      "Reset to the built-in system prompt at any time",
    ],
    steps: [
      "Open Settings → Policy Generation Prompt.",
      "Review the default system prompt, then edit it to match your requirements.",
      "Set sentences per section and check the token usage panel for remaining budget.",
      "Save. The next AI policy generation for this organisation uses the new system prompt.",
      "Use Reset to Default if you want to go back to the built-in prompt.",
    ],
    inputs: ["Custom system prompt"],
    outputs: ["Organisation policy generation prompt"],
    connections: ["Settings", "Policy Store (AI Generate)"],
  },
  {
    id: "tenant-email-templates",
    roles: ["tenant_admin"],
    icon: Mail,
    color: "#0284c7",
    title: "Email Templates",
    location: "Settings → Email Templates",
    path: "/tenant/email-templates",
    purpose: "Make system emails look like they come from your organisation.",
    features: ["Colour presets", "Logo text", "Footer message", "Live preview", "Test send"],
    steps: [
      "Open Email Templates.",
      "Pick colours and enter your organisation name.",
      "Preview on the right as you type.",
      "Send a test email, then Save Template.",
    ],
    inputs: ["Branding choices", "SMTP configured"],
    outputs: ["Branded notification emails"],
    connections: ["Settings", "Email Logs"],
  },
  {
    id: "user-home",
    roles: ["user"],
    icon: Home,
    color: "#0ea5e9",
    title: "Home — Your Starting Point",
    location: "Top menu → Home",
    path: "/user",
    purpose:
      "Your personal compliance dashboard — see progress, the roadmap, and what to do next.",
    features: [
      "Overall progress percentage",
      "Road to Compliance timeline (Assess → PIA → ROPA → TPRM → Breach)",
      "Smart recommendations for your next task",
      "Countdown to DPDP deadline (13 May 2027)",
    ],
    steps: [
      "Log in and land on Home.",
      "Check your progress and days remaining.",
      "Read Smart Recommendations — click a suggestion to jump to that module.",
      "Return here anytime to see what's left to do.",
    ],
    inputs: ["Your assessment progress"],
    outputs: ["Prioritised next actions"],
    connections: ["All user modules", "Dashboard", "Reports"],
  },
  {
    id: "user-dashboard",
    roles: ["user"],
    icon: LayoutDashboard,
    color: "#10b981",
    title: "Dashboard — Live Numbers",
    location: "Top menu → Dashboard",
    path: "/user/dashboard",
    purpose:
      "See live stats for consent, cookies, DSAR, DSPM, assessments, and more — all in one screen.",
    features: [
      "Overall compliance score",
      "Consent, Cookie, and DSAR tiles",
      "ROPA, Gap Assessment, and DSPM progress",
      "DPIA, Third Parties, and Privacy Notices",
      "Quick Actions to start common tasks",
    ],
    steps: [
      "Open Dashboard from the top menu.",
      "Scan the tiles — green is good, gaps need attention.",
      "Click a tile for more detail where available.",
      "Use Quick Actions at the bottom to jump straight into a form or report.",
    ],
    inputs: ["Integration data", "Form submission counts"],
    outputs: ["Real-time compliance metrics"],
    connections: ["Consent Variance", "DSAR", "Reports"],
  },
  {
    id: "asset-tracker",
    roles: ["user"],
    icon: Server,
    color: "#2563eb",
    title: "Asset Tracker",
    location: "Top menu → Asset Tracker",
    path: "/user/asset-tracker",
    purpose:
      "Keep a register of servers, databases, and systems that store or process personal data — organised by department.",
    features: [
      "Add assets manually or bulk import from CSV",
      "Track IP address, database name, and department",
      "Search, filter, and export your asset list",
      "Activate or deactivate entries",
    ],
    steps: [
      "Open Asset Tracker from the top menu.",
      "Select a department, then Add Asset or Bulk Upload.",
      "Fill in system details (IP, database name, etc.).",
      "Review and export the list when auditors ask what systems hold personal data.",
    ],
    inputs: ["Asset details", "Department", "CSV file (optional)"],
    outputs: ["IT asset register", "CSV export"],
    connections: ["ROPA", "DSPM", "Reports"],
  },
  {
    id: "gap-assessment",
    roles: ["user"],
    icon: SearchCheck,
    color: "#6366f1",
    title: "Gap Assessment",
    location: "Assessment → Gap Assessment",
    path: "/user/gap-assessment",
    purpose:
      "Answer questions about your department's privacy readiness — organised into 10 DPDP topic sections.",
    features: [
      "Section-by-section questionnaire (Yes / No / Partial)",
      "Auto-save as you work",
      "Comments and evidence attachments",
      "Policy recommendations when you mark gaps",
      "Submission history",
    ],
    steps: [
      "Open Assessment → Gap Assessment.",
      "Select your department.",
      "Work through each section in the left sidebar.",
      "Add comments or upload evidence where helpful.",
      "Submit on the last section when all required answers are complete.",
    ],
    inputs: ["Answers", "Department", "Comments", "Attachments"],
    outputs: ["Submitted assessment", "Gap scores", "Policy suggestions"],
    connections: ["Gap Assessment Admin", "Policy Store", "Reports", "Home"],
  },
  {
    id: "ropa",
    roles: ["user"],
    icon: FileText,
    color: "#2563eb",
    title: "ROPA — Record of Processing Activities",
    location: "Assessment → ROPA",
    path: "/user/ropa",
    purpose:
      "Document what personal data your department processes, why, how long you keep it, and who you share it with.",
    features: [
      "Manual register (12 sections) or automated submissions view",
      "Draft and submit workflow",
      "Submission history and PDF export",
      "Department-scoped records",
    ],
    steps: [
      "Open Assessment → ROPA.",
      "Choose Manual Register to fill in the 12-section form, or view Automated submissions if connected.",
      "Complete each section — use Next to move through.",
      "Submit when all required fields are filled.",
      "Check Submissions to see past records.",
    ],
    inputs: ["Processing details", "Data categories", "Legal basis", "Retention info"],
    outputs: ["ROPA records", "Audit-ready documentation"],
    connections: ["Form Questions", "User Responses", "Reports", "PIA", "Asset Tracker"],
  },
  {
    id: "pia",
    roles: ["user"],
    icon: ShieldPlus,
    color: "#818cf8",
    title: "PIA — Privacy Impact Assessment",
    location: "Assessment → PIA",
    path: "/user/pia",
    purpose:
      "Evaluate projects or processing that may pose higher privacy risk — and document how you will reduce that risk.",
    features: [
      "6-step guided form",
      "Live risk score (likelihood × impact)",
      "Legal basis selection",
      "Draft/submit and submission history",
    ],
    steps: [
      "Open Assessment → PIA.",
      "Select your department.",
      "Walk through all 6 steps: General Info, Data Details, Data Flow, Risk, Vendor & Controls, Safeguards.",
      "Submit on the last step.",
    ],
    inputs: ["Project description", "Risk factors", "Mitigation controls"],
    outputs: ["PIA records", "Risk documentation"],
    connections: ["ROPA", "Gap Assessment", "PIA Tracking", "Reports"],
  },
  {
    id: "tprm",
    roles: ["user"],
    icon: Handshake,
    color: "#0ea5e9",
    title: "TPRM — Third-Party Risk Management",
    location: "Assessment → TPRM",
    path: "/user/tprm",
    purpose:
      "Assess vendors and partners who handle personal data on your behalf — before you share data with them.",
    features: [
      "7-section vendor questionnaire",
      "Live risk score panel",
      "Evidence attachments",
      "Draft/submit and history",
    ],
    steps: [
      "Open Assessment → TPRM.",
      "Select department and start with General Information.",
      "Answer all sections — upload vendor documents if needed.",
      "Review the risk score in the sidebar before submitting.",
    ],
    inputs: ["Vendor details", "Risk answers", "Attachments"],
    outputs: ["Vendor risk assessments"],
    connections: ["ROPA", "TPRM Tracking", "Reports"],
  },
  {
    id: "breach",
    roles: ["user"],
    icon: AlertTriangle,
    color: "#dc2626",
    title: "Breach Management",
    location: "Top menu → Breach Management",
    path: "/user/breach-management",
    purpose:
      "Report data breaches, track the 72-hour notification clock, and follow the DPDP response steps.",
    features: [
      "Log new breach incidents",
      "Severity and impact details",
      "72-hour DPB notification countdown",
      "7-stage response timeline",
      "Evidence attachments and status tracking",
    ],
    steps: [
      "Click Log New Breach as soon as you detect an incident.",
      "Enter what happened, what data was affected, and how many people are impacted.",
      "Follow the response timeline — DPO notification, DPB notification, data principal notice.",
      "Update status as you investigate and remediate.",
    ],
    inputs: ["Incident details", "Severity", "Impact counts", "Files"],
    outputs: ["Breach records", "Notification audit trail"],
    connections: ["Reports", "Home", "Policy Store", "Settings reminders"],
  },
  {
    id: "consent-variance",
    roles: ["user"],
    icon: ArrowLeftRight,
    color: "#7c3aed",
    title: "Consent Variance",
    location: "Top menu → Consent Variance",
    path: "/user/consent-variance",
    purpose:
      "Compare what people consented to with what data you actually collect — find mismatches that need fixing.",
    features: [
      "Department-level comparison",
      "Matched vs. unmatched data types",
      "Visual summary of gaps",
      "Reconsent tracking",
    ],
    steps: [
      "Open Consent Variance.",
      "Select a department.",
      "Review rows marked as mismatched — these may need updated consent.",
      "Share the list with marketing or legal to action reconsent.",
    ],
    inputs: ["Consent integration data", "DSPM inventory data"],
    outputs: ["Variance report", "Reconsent action list"],
    connections: ["Dashboard", "Reports", "Consent integration"],
  },
  {
    id: "dsar-records",
    roles: ["user"],
    icon: Inbox,
    color: "#059669",
    title: "DSAR — Data Subject Requests",
    location: "Top menu → DSAR",
    path: "/user/dsar",
    purpose:
      "View and manage requests from individuals who want access to, correction of, or deletion of their personal data.",
    features: [
      "List of all DSAR requests",
      "Filter by status (Open, Pending, In Process, Approved, etc.)",
      "Search by name, email, or request ID",
      "Refresh to pull latest from your DSAR system",
    ],
    steps: [
      "Open DSAR from the top menu.",
      "Use filters to see open or overdue requests.",
      "Click a request to view full details.",
      "Update status as your team processes each request.",
    ],
    inputs: ["DSAR integration data"],
    outputs: ["Request tracking", "Status visibility"],
    connections: ["Dashboard", "Reports"],
  },
  {
    id: "policy-store",
    roles: ["user"],
    icon: BookOpen,
    color: "#6366f1",
    title: "Policy Store",
    location: "Top menu → Policy Store",
    path: "/user/policy-store",
    purpose:
      "Store and manage privacy policies — use templates, upload your own, or let AI draft a starting version.",
    features: [
      "Built-in DPDP policy templates",
      "Upload custom documents",
      "AI-powered draft generation",
      "PDF export",
      "Recommendations from Gap Assessment gaps",
    ],
    steps: [
      "Browse existing policies or click Upload Policy.",
      "Use AI Generate for a draft (requires AI set up in Organisation Settings).",
      "Review and edit before sharing.",
      "Download PDF for distribution to staff or website.",
    ],
    inputs: ["Templates", "Uploads", "AI prompts", "Gap results"],
    outputs: ["Policy documents", "PDF exports"],
    connections: ["Gap Assessment", "Organisation Settings", "Breach Management"],
  },
  {
    id: "reports",
    roles: ["user"],
    icon: BarChart3,
    color: "#d946ef",
    title: "Reports",
    location: "Top menu → Reports",
    path: "/user/reports",
    purpose:
      "Create PDF reports for leadership, auditors, or the board — choose how much detail you need.",
    features: [
      "Executive, Summary, and Detailed report types",
      "Customisable charts and colours",
      "Key Risk Indicators section",
      "Covers Consent, DSAR, Gap, ROPA, PIA, TPRM, DSPM, and more",
    ],
    steps: [
      "Open Reports.",
      "Pick Executive (high level), Summary (balanced), or Detailed (full audit trail).",
      "Adjust chart settings if needed.",
      "Click Export PDF and save the file.",
    ],
    inputs: ["Data from all modules"],
    outputs: ["PDF compliance reports"],
    connections: ["All modules", "Dashboard", "Home"],
  },
  {
    id: "consent-integration",
    roles: ["user", "tenant_admin", "admin"],
    icon: Cookie,
    color: "#d97706",
    title: "Consent & Cookies",
    location: "Dashboard tiles / Reports",
    path: "/user/dashboard",
    purpose:
      "Track consent collection and cookie compliance when your organisation uses the Consent integration.",
    features: [
      "Consent stats on Dashboard",
      "Cookie compliance metrics",
      "Included in Reports",
    ],
    steps: [
      "Super Admin enables the Consent module for your organisation.",
      "Numbers appear on Dashboard once your consent tool is connected.",
      "Use Consent Variance to compare consent with your data inventory.",
      "Include consent metrics in Reports for leadership.",
    ],
    inputs: ["External consent platform data"],
    outputs: ["Consent and cookie metrics"],
    connections: ["Dashboard", "Consent Variance", "Reports"],
  },
  {
    id: "dsar-integration",
    roles: ["user", "tenant_admin", "admin"],
    icon: Scale,
    color: "#059669",
    title: "DSAR Integration",
    location: "Dashboard tile / DSAR page / Reports",
    path: "/user/dsar",
    purpose:
      "Connect your data-subject request system so request counts and status appear in OneDPDP.",
    features: ["DSAR stats on Dashboard", "Full request list on DSAR page", "Report inclusion"],
    steps: [
      "Super Admin enables DSAR for your organisation.",
      "View summary stats on Dashboard.",
      "Manage individual requests on the DSAR page.",
      "Include DSAR metrics in compliance Reports.",
    ],
    inputs: ["External DSAR system data"],
    outputs: ["DSAR request metrics and tracking"],
    connections: ["Dashboard", "DSAR page", "Reports"],
  },
  {
    id: "dspm-integration",
    roles: ["user", "tenant_admin", "admin"],
    icon: Database,
    color: "#6366f1",
    title: "DSPM — Data Discovery",
    location: "Dashboard tile / Consent Variance / Reports",
    path: "/user/dashboard",
    purpose:
      "See what personal data exists across your systems when DSPM (Data Security Posture Management) is connected.",
    features: [
      "PII discovery counts on Dashboard",
      "Feeds Consent Variance comparisons",
      "Included in Reports",
    ],
    steps: [
      "Super Admin enables DSPM for your organisation.",
      "Review discovered data counts on Dashboard.",
      "Use with Consent Variance to find collection without valid consent.",
      "Reference DSPM findings in Detailed Reports.",
    ],
    inputs: ["External DSPM scan data"],
    outputs: ["Data inventory metrics"],
    connections: ["Dashboard", "Consent Variance", "Asset Tracker", "Reports"],
  },
  {
    id: "email-tools",
    roles: ["admin", "tenant_admin"],
    icon: Mail,
    color: "#0284c7",
    title: "Email & Notifications",
    location: "Settings → Email Templates / Email Logs",
    path: "/admin/settings",
    purpose: "Make sure welcome emails, reminders, and alerts actually reach people's inboxes.",
    features: [
      "SMTP configuration",
      "Branded header and footer",
      "Test send",
      "Delivery logs with success/failure status",
    ],
    steps: [
      "Configure SMTP in Settings first.",
      "Customise email appearance in Email Templates.",
      "Send a test email to yourself.",
      "If something fails, check Email Logs for the reason.",
    ],
    inputs: ["SMTP config", "Branding"],
    outputs: ["Working notifications", "Delivery audit trail"],
    connections: ["User creation emails", "Breach reminders"],
  },
];

export const USE_CASES = [
  {
    title: "Brand-new organisation",
    scenario: "Your company just signed up and has never done a formal privacy programme.",
    flow: "Super Admin creates your workspace → Organisation Admin adds departments and users → Team completes Gap Assessment → Admin opens a cycle and tracks progress → Reports show leadership where you stand.",
  },
  {
    title: "Getting ready for an audit",
    scenario: "Internal audit is in six weeks and you need proof of your privacy programme.",
    flow: "Update ROPA and PIA records → Organisation Admin reviews submissions in User Responses → Generate a Detailed Report PDF → Share with auditors along with Policy Store documents.",
  },
  {
    title: "Suspected data breach",
    scenario: "Someone may have accessed customer personal data without permission.",
    flow: "Log the incident in Breach Management immediately → Follow the 72-hour timeline → Notify DPO and prepare DPB notification → Update status as you contain and fix → Include in compliance Reports.",
  },
  {
    title: "Onboarding a new vendor",
    scenario: "A new cloud provider will process customer data for you.",
    flow: "Complete TPRM assessment for the vendor → Organisation Admin reviews in User Responses → Address any gaps → Update vendor clauses in Policy Store.",
  },
  {
    title: "Marketing changed data collection",
    scenario: "A new signup form collects extra fields — you need to check consent still matches.",
    flow: "Open Consent Variance by department → Find mismatched data types → Trigger reconsent with marketing → Confirm numbers improve on Dashboard.",
  },
  {
    title: "Citizen asks for their data",
    scenario: "A customer emails asking for a copy of all personal data you hold about them.",
    flow: "Find the request in DSAR → Track status from Open to Approved → Use Asset Tracker and ROPA to locate relevant systems → Close the request when fulfilled.",
  },
  {
    title: "Mapping IT systems",
    scenario: "You need a list of every server and database that holds personal data.",
    flow: "Use Asset Tracker to register systems by department → Cross-check with ROPA processing records → Export CSV for IT and audit teams.",
  },
];

export const BEST_PRACTICES = [
  {
    title: "Set up the basics first",
    text: "Before anyone starts assessments, create departments, add users, and review the default questions. Good setup saves confusion later.",
  },
  {
    title: "Start with Gap Assessment",
    text: "It shows your baseline maturity and unlocks useful policy recommendations. Most organisations treat it as step one.",
  },
  {
    title: "Use formal assessment cycles",
    text: "Organisation Admins should open cycles with clear start and end dates so everyone knows the deadline and progress is measurable.",
  },
  {
    title: "Save as you go",
    text: "Forms auto-save in most modules, but if you see Save Draft, use it — especially on long ROPA and PIA forms.",
  },
  {
    title: "Set up email early",
    text: "Configure SMTP at platform or organisation level so welcome emails and breach reminders work from day one.",
  },
  {
    title: "Review submissions weekly",
    text: "Organisation Admins should check User Responses regularly to catch incomplete or inconsistent answers before deadlines.",
  },
  {
    title: "Share reports with leadership monthly",
    text: "Executive Reports keep management informed. Use Detailed Reports before formal audits.",
  },
  {
    title: "Keep policies current",
    text: "When Gap Assessment finds new risks or processing changes, update Policy Store so documents match reality.",
  },
  {
    title: "Report breaches quickly",
    text: "Log incidents in Breach Management as soon as you detect them — the 72-hour clock starts from awareness, not from when you finish investigating.",
  },
  {
    title: "Use Help when stuck",
    text: "Open Help → Interactive Tour for a guided walkthrough of any page, or search this documentation for answers.",
  },
];

export const GLOSSARY = [
  {
    term: "DPDP Act",
    definition:
      "India's Digital Personal Data Protection Act, 2023 — the law that sets rules for how organisations must handle personal data. OneDPDP helps you work toward compliance by 13 May 2027.",
  },
  {
    term: "Personal data",
    definition:
      "Any information that can identify a person — name, email, phone, Aadhaar, photos, IP address, etc.",
  },
  {
    term: "Data Principal",
    definition: "The person whose personal data you hold — your customer, employee, or website visitor.",
  },
  {
    term: "Data Fiduciary",
    definition:
      "The organisation that decides why and how personal data is processed — usually your company.",
  },
  {
    term: "Gap Assessment",
    definition:
      "A questionnaire that checks how ready your organisation is on DPDP topics like consent, security, and breach response.",
  },
  {
    term: "ROPA",
    definition:
      "Record of Processing Activities — a register listing what data you process, why, how long you keep it, and who you share it with.",
  },
  {
    term: "PIA / DPIA",
    definition:
      "Privacy (or Data Protection) Impact Assessment — a deeper review when processing might pose higher risk to people's privacy.",
  },
  {
    term: "TPRM",
    definition:
      "Third-Party Risk Management — assessing vendors and partners who handle personal data on your behalf.",
  },
  {
    term: "DSAR",
    definition:
      "Data Subject Access Request — when a person asks to see, correct, or delete their personal data.",
  },
  {
    term: "Consent",
    definition:
      "Permission from a Data Principal to process their personal data for a specific purpose — must be freely given and informed under DPDP.",
  },
  {
    term: "Consent Variance",
    definition:
      "A comparison showing where your actual data collection does not match what people consented to — often needs a fix.",
  },
  {
    term: "DSPM",
    definition:
      "Data Security Posture Management — tools that scan systems to find where personal data lives.",
  },
  {
    term: "Breach",
    definition:
      "When personal data is accessed, lost, or shared without proper authorisation — may require notification to authorities within 72 hours.",
  },
  {
    term: "DPB",
    definition: "Data Protection Board — the authority under DPDP that organisations may need to notify about serious breaches.",
  },
  {
    term: "Assessment cycle",
    definition:
      "A time window (start and end date) when your Organisation Admin accepts submissions — outside an open cycle, Submit may be disabled.",
  },
  {
    term: "Organisation login link",
    definition:
      "The web address your admin shares for sign-in. Bookmark it — you will use it every time you log in.",
  },
  {
    term: "License / user slots",
    definition:
      "How many people your organisation can add to OneDPDP. Contact your Super Admin if you run out of slots.",
  },
  {
    term: "SMTP",
    definition:
      "The email server settings that let OneDPDP send welcome emails, reminders, and alerts. Your IT team can provide these details.",
  },
];

export const FAQS = [
  {
    q: "How do I log in?",
    a: "Use the login link your admin gave you. Enter your email and password. Super Admins use a separate platform login without an organisation link. Passwords are case-sensitive.",
  },
  {
    q: "What is the organisation login link?",
    a: "It is a web address unique to your company. It tells OneDPDP which organisation you belong to. Bookmark it — you will use it every time you sign in.",
  },
  {
    q: "I forgot my password — what do I do?",
    a: "Ask your Organisation Admin to reset it from Tenant Admins → Users. They can set a new password for you.",
  },
  {
    q: "Why can't I create more users?",
    a: "Your plan has a user limit. Check License to see slots used vs. available. Contact your Super Admin or account manager to increase the limit.",
  },
  {
    q: "What's the difference between Home and Dashboard?",
    a: "Home is your personal roadmap — what to do next and overall progress. Dashboard shows live numbers (consent, DSAR, assessments, etc.) across the organisation.",
  },
  {
    q: "Can we change the assessment questions?",
    a: "Yes. Organisation Admins can edit questions under Tenant Admins → Form Questions for Gap Assessment, ROPA, PIA, and TPRM.",
  },
  {
    q: "Why can't I submit my assessment?",
    a: "Usually one of three reasons: required fields are empty, no assessment cycle is open, or your session expired. Fill all required fields, ask your admin to open a cycle, or log out and back in.",
  },
  {
    q: "Can I edit after submitting?",
    a: "Submitted work is locked. Your Organisation Admin can reopen it from Gap Admin (or PIA/TPRM Tracking) if a correction is genuinely needed.",
  },
  {
    q: "Where do Consent and DSAR numbers come from?",
    a: "They come from connected integrations, enabled by your Super Admin. Stats appear on Dashboard and in Reports. Manage individual DSAR requests on the DSAR page.",
  },
  {
    q: "How do I download a compliance report?",
    a: "Go to Reports → choose Executive, Summary, or Detailed → adjust charts if you like → click Export PDF.",
  },
  {
    q: "What is the DPDP deadline?",
    a: "The platform tracks progress toward 13 May 2027 — shown on your Home page countdown.",
  },
  {
    q: "Why is AI Generate greyed out in Policy Store?",
    a: "Your Organisation Admin must add an AI provider (API key) in Settings first. Once connected and tested, AI Generate becomes available.",
  },
  {
    q: "Why don't I see a menu item others have?",
    a: "Menus depend on your role (Team Member vs. Organisation Admin) and which modules your organisation purchased. Ask your admin if a whole module is missing.",
  },
  {
    q: "What is Asset Tracker for?",
    a: "It is a register of IT systems (servers, databases) that store personal data — useful for audits and linking to ROPA records.",
  },
  {
    q: "What happens at the 72-hour breach mark?",
    a: "Under DPDP, serious breaches may require notifying the Data Protection Board within a prescribed period. OneDPDP shows a countdown per incident so you can prioritise response.",
  },
];

export const TROUBLESHOOTING = [
  {
    issue: "Login says invalid credentials",
    causes: [
      "Wrong email or password",
      "Using another organisation's login link",
      "Account deactivated by admin",
    ],
    fix: "Double-check email and password (case matters). Confirm you are on YOUR organisation's login link. Ask your Organisation Admin to verify your account is Active.",
  },
  {
    issue: "Submit button is disabled or missing",
    causes: [
      "Required questions unanswered",
      "No open assessment cycle",
      "Session timed out",
    ],
    fix: "Complete all required fields (often marked with *). Ask your Organisation Admin to open or extend an assessment cycle. Log out and back in if the page seems stuck.",
  },
  {
    issue: "Emails are not arriving",
    causes: [
      "SMTP not configured or disabled",
      "Wrong email server credentials",
      "Message caught in spam folder",
    ],
    fix: "Organisation Admin: Settings → enable SMTP → Send Test. Check Email Logs for errors. Ask recipients to check spam. Platform Admin: verify platform SMTP as fallback.",
  },
  {
    issue: "Dashboard shows 0 for Consent, DSAR, or DSPM",
    causes: [
      "Module not enabled for your organisation",
      "External system not connected yet",
      "No data synced",
    ],
    fix: "Ask Super Admin to enable the module. Confirm your external consent/DSAR/DSPM tool is connected. Click Refresh on Dashboard. Note: Gap and ROPA tiles show 0 until someone submits forms.",
  },
  {
    issue: "Cannot add a new user",
    causes: ["All license slots are used"],
    fix: "Check License for Slots Left. Deactivate unused accounts or ask Super Admin to increase your limit.",
  },
  {
    issue: "AI policy generation fails",
    causes: ["No AI provider configured", "Invalid or expired API key"],
    fix: "Organisation Admin → Settings → AI Provider → add provider and click Test. Use a valid API key from your AI vendor.",
  },
  {
    issue: "Submission stuck below 100%",
    causes: ["One or more required fields empty in a section"],
    fix: "Check section count (e.g. 10/12 filled). Open each incomplete section and find the blank required field. Save and progress should update immediately.",
  },
  {
    issue: "Page won't load or shows network error",
    causes: ["Connection issue", "Session expired", "Browser cache"],
    fix: "Refresh the page. Log out and log back in. Try another browser or clear cache. Contact IT if the problem persists.",
  },
  {
    issue: "Missing menu item I expect to see",
    causes: [
      "Wrong role logged in",
      "Module not licensed for your organisation",
    ],
    fix: "Check your role badge (top right). Confirm with Organisation Admin which modules are enabled on your plan.",
  },
];

export const LIFECYCLE_STAGES = [
  {
    id: "assess",
    label: "Discover & Assess",
    icon: SearchCheck,
    color: "#6366f1",
    path: "/user/gap-assessment",
  },
  {
    id: "ropa",
    label: "Map Processing",
    icon: FileText,
    color: "#2563eb",
    path: "/user/ropa",
  },
  {
    id: "pia",
    label: "Impact Assessment",
    icon: ShieldPlus,
    color: "#818cf8",
    path: "/user/pia",
  },
  {
    id: "tprm",
    label: "Vendor Risk",
    icon: Handshake,
    color: "#0ea5e9",
    path: "/user/tprm",
  },
  {
    id: "assets",
    label: "Track Assets",
    icon: Server,
    color: "#2563eb",
    path: "/user/asset-tracker",
  },
  {
    id: "consent",
    label: "Consent & Notice",
    icon: Cookie,
    color: "#d97706",
    path: "/user/consent-variance",
  },
  {
    id: "dsar",
    label: "Data Subject Rights",
    icon: Scale,
    color: "#059669",
    path: "/user/dsar",
  },
  {
    id: "breach",
    label: "Breach Readiness",
    icon: AlertTriangle,
    color: "#dc2626",
    path: "/user/breach-management",
  },
  {
    id: "monitor",
    label: "Monitor & Report",
    icon: TrendingUp,
    color: "#7c3aed",
    path: "/user/reports",
  },
];

export function buildHelpGuidePdfSections(role = "user") {
  const roleInfo = ROLES.find((r) => r.id === role) || ROLES[2];
  const modules = MODULES.filter((m) => m.roles.includes(role));
  const checklist =
    GETTING_STARTED.checklists.find((c) => c.role === role) ||
    GETTING_STARTED.checklists[2];

  return [
    {
      heading: "System Overview",
      rows: [
        { label: "Platform", value: SYSTEM_OVERVIEW.title },
        { label: "Tagline", value: SYSTEM_OVERVIEW.tagline },
        { label: "Description", value: SYSTEM_OVERVIEW.description },
        { label: "DPDP Deadline", value: SYSTEM_OVERVIEW.deadline },
        { label: "Generated for role", value: roleInfo.label },
        ...SYSTEM_OVERVIEW.pillars.map((p) => ({
          label: p.title,
          value: p.text,
        })),
      ],
    },
    {
      heading: "Getting Started",
      rows: checklist.steps.map((step, i) => ({
        label: `Step ${i + 1}`,
        value: step,
      })),
    },
    {
      heading: "User Roles",
      table: {
        headers: ["Role", "Description", "Key Responsibilities"],
        rows: ROLES.map((r) => [
          r.label,
          r.description,
          r.responsibilities.join("; "),
        ]),
      },
    },
    {
      heading: "End-to-End Journey",
      table: {
        headers: ["Step", "Phase", "Role", "Summary", "Details"],
        rows: WORKFLOW_PHASES.map((p) => [
          String(p.step),
          p.title,
          p.role,
          p.summary,
          p.details,
        ]),
      },
    },
    {
      heading: `Features Guide (${roleInfo.label})`,
      table: {
        headers: ["Feature", "Where to Find It", "Purpose", "Key Capabilities"],
        rows: modules.map((m) => [
          m.title,
          m.location,
          m.purpose,
          m.features.join("; "),
        ]),
      },
    },
    {
      heading: "Real-Life Examples",
      table: {
        headers: ["Example", "Situation", "Suggested Flow"],
        rows: USE_CASES.map((uc) => [uc.title, uc.scenario, uc.flow]),
      },
    },
    {
      heading: "Tips & Best Practices",
      rows: BEST_PRACTICES.map((bp) => ({ label: bp.title, value: bp.text })),
    },
    {
      heading: "Simple Glossary",
      rows: GLOSSARY.map((g) => ({ label: g.term, value: g.definition })),
    },
    {
      heading: "Common Questions",
      qa: FAQS.map((f) => ({ question: f.q, answer: f.a })),
    },
    {
      heading: "Fix Common Problems",
      table: {
        headers: ["Problem", "Possible Causes", "How to Fix"],
        rows: TROUBLESHOOTING.map((t) => [t.issue, t.causes.join("; "), t.fix]),
      },
    },
  ];
}
